import { vehicleSelected } from 'Core/actions/fitmentSearch';
import { assignLocation, pushToHistory, replaceInHistory, replaceLocation } from 'Core/actions/redirect';
import { setServerRedirect } from 'Core/actions/response';
import { type EpicArg, epicFromHandlers } from 'Core/epics/common';
import history from 'Core/history';
import { vehicleSelectionSelector } from 'Core/selectors/fitmentSearch/index';
import { serverRedirectSelector } from 'Core/selectors/search';
import type { AppDispatch, ReduxStateType } from 'Core/store';
import type { SelectionEntry } from 'Models/index';
import fitmentSearchConfig from 'Models/uiConfig/fitmentSearchConfig';
import requestConfig from 'Models/uiConfig/requestConfig';
import { getUriFromRequest, getUriWithVehicle } from 'Modules/converter';
import type { ServerModel } from 'Modules/serverApi/types';
import { addPlatformRoute } from 'Utils/url';

type RedirectArg = Pick<EpicArg, 'dispatch'> & { action: Partial<EpicArg['action']> };

const { hasHtmxResults, isHtmxMode } = requestConfig;

export default epicFromHandlers(
  {
    [assignLocation.type]: ({ state, action: { payload } }) => {
      const serverRedirect = serverRedirectSelector(state);
      window.location.assign(getNavigationUrl(serverRedirect || payload, state));
    },
    [replaceLocation.type]: ({ state, action: { payload } }) =>
      window.location.replace(getNavigationUrl(payload, state)),
    [pushToHistory.type]: ({
      action: {
        payload: { location, state },
      },
    }) =>
      // sometimes history manipulations force style recalculation so it's better to do it a bit later
      void setTimeout(() => {
        history.push(location, state);
        window.ga?.('send', 'pageview', window.location.pathname + window.location.search);
      }, 0),
    [replaceInHistory.type]: ({
      action: {
        payload: { location, state },
      },
    }) => {
      if (isHtmxMode && hasHtmxResults) {
        return;
      }

      void setTimeout(() => {
        history.replace(location, state);
      }, 0);
    },
  },
  'redirect',
);

function getNavigationUrl(url: string, state: ReduxStateType): string {
  return getUriWithVehicle(addPlatformRoute(url), vehicleSelectionSelector(state));
}

export function tryResponseRedirect(arg: RedirectArg, response: ServerModel.SearchResponse): boolean {
  const {
    dispatch,
    action: { meta: { doNotRedirectOnVehicleSelect, onInit } = {}, type: actionType },
  } = arg;

  const isVehicleSelectedAction = actionType === vehicleSelected.type;

  if (isVehicleSelectedAction && (onInit || doNotRedirectOnVehicleSelect)) {
    return false;
  }

  const firstAction = response.Actions?.[0];
  if (firstAction?.Type === 'Redirect' && firstAction.RedirectUrl) {
    const redirectUrl = addPlatformRoute(firstAction.RedirectUrl);
    if (window.location.pathname !== redirectUrl) {
      dispatch(setServerRedirect(redirectUrl));
      dispatch(assignLocation(redirectUrl));
      return true;
    }

    return false;
  }

  const { vehicleLandingRedirectToSearch } = response.Extra ?? {};
  const redirectToSearch =
    typeof vehicleLandingRedirectToSearch === 'boolean' ? vehicleLandingRedirectToSearch : false;
  return isVehicleSelectedAction ? tryVehicleRedirect(arg, redirectToSearch) : false;
}

export function tryVehicleRedirect(arg: RedirectArg, vehicleLandingRedirectToSearch: boolean): boolean {
  const {
    dispatch,
    action: {
      payload: selectedVehicle,
      meta: {
        isSpecifyDialogOpened,
        isSelectedFromDialog,
        isSelectedFromGarage,
        isSelectedFromGarageDropdowns,
        isSelectedFromHeader,
        isSelectedFromSearch,
        isVehicleSelectionForced,
        isVehicleAutoSelected,
        redirectUrl,
      } = {},
    },
  } = arg;

  const {
    categorySelectionPageUrl,
    isAutoRedirectDisabled,
    isCategorySelectionPageRedirectEnabled,
    isOnCategorySelectionPage,
  } = fitmentSearchConfig;

  const isSelectedFromIsolatedHeader = isSelectedFromHeader && !isOnCategorySelectionPage;
  const isSelectedFromIsolatedDialog =
    isSelectedFromDialog && !isSpecifyDialogOpened && !isVehicleSelectionForced;
  const isSelectedFromSearchWithRedirect = isSelectedFromSearch && redirectUrl;

  const isWidgetRedirectAllowed =
    isSelectedFromIsolatedHeader ||
    isSelectedFromIsolatedDialog ||
    isSelectedFromGarageDropdowns ||
    isSelectedFromGarage ||
    isSelectedFromSearchWithRedirect;

  const isVehicleSelectionRedirectAllowed =
    !isVehicleAutoSelected || (isVehicleAutoSelected && !isAutoRedirectDisabled);

  if ((isWidgetRedirectAllowed || vehicleLandingRedirectToSearch) && isVehicleSelectionRedirectAllowed) {
    const selectedVehicleSelection = selectedVehicle.selection;

    if (redirectUrl) {
      return tryVehicleRedirectByUrl(dispatch, redirectUrl, selectedVehicleSelection);
    }

    if (!isCategorySelectionPageRedirectEnabled || !categorySelectionPageUrl) {
      const searchPageRedirectUri = getUriFromRequest(
        { selection: selectedVehicleSelection },
        { goToSearchPage: true },
      );

      if (window.location.pathname !== searchPageRedirectUri.pathname) {
        dispatch(assignLocation(searchPageRedirectUri.href));
        return true;
      }

      return false;
    }

    if (isCategorySelectionPageRedirectEnabled && !isOnCategorySelectionPage) {
      dispatch(
        assignLocation(
          getUriFromRequest({ selection: selectedVehicleSelection }, { pathname: categorySelectionPageUrl })
            .href,
        ),
      );

      return true;
    }
  }

  return false;
}

function tryVehicleRedirectByUrl(
  dispatch: AppDispatch,
  redirectUrl: string,
  selectedVehicleSelection: SelectionEntry[],
) {
  if (redirectUrl && window.location.pathname !== redirectUrl) {
    dispatch(
      assignLocation(
        getUriFromRequest({ selection: selectedVehicleSelection }, { pathname: redirectUrl }).href,
      ),
    );

    return true;
  }

  return false;
}
